/**
 * Payouts — partner withdrawal center.
 *
 * "Refined dark command-center" treatment (matches Balance): atmospheric glow,
 * orchestrated load, a withdrawal hero with the available figure dominant,
 * a hold-release forecast, the auto-payout control, and a type-coded history
 * of every request with a per-payout detail timeline.
 */
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Bitcoin,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock,
  CreditCard,
  Hourglass,
  Landmark,
  Lock,
  Send,
  ShieldCheck,
  Sparkles,
  Wallet,
  XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Button,
  Card,
  CopyButton,
  FieldError,
  Input,
  Label,
  Modal,
  PageHeader,
  ProgressBar,
  Skeleton,
} from '@/components/ui';
import AutoPayoutToggle from '@/components/dashboard/AutoPayoutToggle';
import { partnerApi } from '@/lib/api-affiliate';
import { formatCents, formatDate } from '@/lib/format';
import type { Payout, PayoutMethod, PayoutStatus } from '@/lib/types';

// ─────────────────────────────────────────────────────────────────────────
//  Animation helpers (mirrors Balance page)
// ─────────────────────────────────────────────────────────────────────────

const rise = (ms: number): CSSProperties => ({
  animation: 'payRise 0.55s var(--ease-out) both',
  animationDelay: `${ms}ms`,
});
const fade = (ms: number): CSSProperties => ({
  animation: 'payFade 0.5s var(--ease-out) both',
  animationDelay: `${ms}ms`,
});

// ─────────────────────────────────────────────────────────────────────────
//  Meta maps
// ─────────────────────────────────────────────────────────────────────────

interface MethodMeta {
  label: string;
  short: string;
  Icon: LucideIcon;
  /** True for crypto methods that benefit from a network/memo field. */
  crypto: boolean;
}

const METHOD_META: Record<PayoutMethod, MethodMeta> = {
  usdt_trc20: { label: 'USDT · TRC20', short: 'USDT', Icon: CircleDollarSign, crypto: true },
  usdt_bep20: { label: 'USDT · BEP20', short: 'USDT', Icon: CircleDollarSign, crypto: true },
  // Legacy-методы — оставлены только для корректного показа старых выплат в истории.
  usdt_erc20: { label: 'USDT · ERC20', short: 'USDT', Icon: CircleDollarSign, crypto: true },
  btc: { label: 'Bitcoin', short: 'BTC', Icon: Bitcoin, crypto: true },
  card: { label: 'Банковская карта', short: 'Карта', Icon: CreditCard, crypto: false },
  bank: { label: 'Банковский перевод', short: 'Банк', Icon: Landmark, crypto: false },
};

// Партнёр может запросить выплату только в USDT TRC20 или BEP20.
type RequestMethod = 'usdt_trc20' | 'usdt_bep20';
const METHOD_ORDER: RequestMethod[] = ['usdt_trc20', 'usdt_bep20'];

function methodMeta(m: string | null | undefined): MethodMeta {
  if (m && m in METHOD_META) return METHOD_META[m as PayoutMethod];
  return { label: m ?? '—', short: m ?? '—', Icon: Wallet, crypto: false };
}

interface StatusMeta {
  label: string;
  tint: string;
  Icon: LucideIcon;
  /** One-line explanation shown in the detail modal. */
  hint: string;
}

const STATUS_META: Record<PayoutStatus, StatusMeta> = {
  pending: {
    label: 'На рассмотрении',
    tint: 'var(--warning)',
    Icon: Hourglass,
    hint: 'Заявка отправлена и ждёт проверки менеджером.',
  },
  approved: {
    label: 'Одобрена',
    tint: 'var(--info)',
    Icon: ShieldCheck,
    hint: 'Заявка одобрена — средства скоро будут отправлены.',
  },
  paid: {
    label: 'Выплачена',
    tint: 'var(--success)',
    Icon: CheckCircle2,
    hint: 'Средства отправлены на указанные реквизиты.',
  },
  declined: {
    label: 'Отклонена',
    tint: 'var(--danger)',
    Icon: XCircle,
    hint: 'Заявка отклонена. Средства вернулись на баланс.',
  },
  failed: {
    label: 'Ошибка перевода',
    tint: 'var(--danger)',
    Icon: AlertTriangle,
    hint: 'Перевод не прошёл. Менеджер повторит выплату.',
  },
};

function statusMeta(s: string | null | undefined): StatusMeta {
  if (s && s in STATUS_META) return STATUS_META[s as PayoutStatus];
  return { label: s ?? '—', tint: 'var(--text-muted)', Icon: Clock, hint: '' };
}

// ─────────────────────────────────────────────────────────────────────────
//  Misc helpers
// ─────────────────────────────────────────────────────────────────────────

/** Truncate a long wallet address / IBAN, keeping head & tail. */
function shortAddr(a: string | null | undefined): string {
  if (!a) return '—';
  if (a.length <= 22) return a;
  return `${a.slice(0, 12)}…${a.slice(-8)}`;
}

/** Whole-day distance from today; negative = past. */
function dayDelta(iso: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

function relativeDay(iso: string): string {
  const d = dayDelta(iso);
  if (d <= 0) return 'сегодня';
  if (d === 1) return 'завтра';
  if (d >= 2 && d <= 4) return `через ${d} дня`;
  return `через ${d} дней`;
}

function formatReleaseDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long' });
}

// ─────────────────────────────────────────────────────────────────────────
//  Hero — available to withdraw
// ─────────────────────────────────────────────────────────────────────────

function HeroSubStat({
  label,
  value,
  tint,
  Icon,
}: {
  label: string;
  value: string;
  tint: string;
  Icon: LucideIcon;
}) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <span
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
        style={{
          backgroundColor: `color-mix(in oklch, ${tint} 14%, transparent)`,
          border: `1px solid color-mix(in oklch, ${tint} 26%, transparent)`,
          color: tint,
        }}
        aria-hidden
      >
        <Icon size={14} strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
          {label}
        </div>
        <div
          className="mt-0.5 text-[17px] font-bold tabular-nums leading-none text-[var(--text)]"
          style={{ letterSpacing: 'var(--tracking-tight)' }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function PayoutHero({
  availableCents,
  holdCents,
  inProgressCents,
  paidTotalCents,
  minPayoutCents,
  canWithdraw,
  onRequest,
  style,
}: {
  availableCents: number;
  holdCents: number;
  inProgressCents: number;
  paidTotalCents: number;
  minPayoutCents: number;
  canWithdraw: boolean;
  onRequest: () => void;
  style?: CSSProperties;
}) {
  const belowMin = availableCents < minPayoutCents;
  const remainingToMin = Math.max(0, minPayoutCents - availableCents);
  const minProgress = minPayoutCents > 0 ? availableCents / minPayoutCents : 1;

  return (
    <Card
      className="overflow-hidden"
      style={{ ...style, borderColor: 'color-mix(in oklch, var(--accent) 24%, var(--border))' }}
    >
      <div
        className="relative p-5 md:p-6"
        style={{
          background:
            'radial-gradient(130% 150% at 0% 0%, color-mix(in oklch, var(--accent) 20%, var(--bg-raised)) 0%, var(--bg-raised) 60%)',
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full opacity-60"
          style={{
            background:
              'radial-gradient(circle, color-mix(in oklch, var(--accent) 32%, transparent), transparent 70%)',
          }}
        />

        <div className="relative flex flex-wrap items-end justify-between gap-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)]"
                style={{
                  backgroundColor: 'color-mix(in oklch, var(--accent) 16%, transparent)',
                  border: '1px solid color-mix(in oklch, var(--accent) 30%, transparent)',
                  color: 'var(--accent-bright)',
                }}
                aria-hidden
              >
                <Wallet size={15} strokeWidth={2} />
              </span>
              <span className="text-[11px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                Доступно к выводу
              </span>
            </div>
            <div
              className="mt-2 text-[32px] md:text-[42px] font-bold tabular-nums leading-none text-[var(--text)]"
              style={{ letterSpacing: 'var(--tracking-tight)' }}
            >
              {formatCents(availableCents)}
            </div>
          </div>

          <Button
            variant={canWithdraw ? 'gradient' : 'secondary'}
            size="lg"
            disabled={!canWithdraw}
            onClick={onRequest}
          >
            <Send size={16} strokeWidth={2.5} />
            Запросить выплату
            {canWithdraw && <ArrowRight size={16} strokeWidth={2.5} />}
          </Button>
        </div>

        {/* Min-payout progress — only while the partner cannot withdraw yet. */}
        {belowMin && minPayoutCents > 0 && (
          <div className="relative mt-5 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[color-mix(in_oklch,var(--warning)_7%,var(--bg-raised))] p-3.5">
            <ProgressBar
              value={minProgress}
              tone="warning"
              label={`До минимальной выплаты — ${formatCents(minPayoutCents)}`}
              valueLabel={`${formatCents(availableCents)} / ${formatCents(minPayoutCents)}`}
            />
            <p className="mt-2 text-[12px] text-[var(--text-muted)]">
              Наберите ещё{' '}
              <span className="font-semibold text-[var(--warning)] tabular-nums">
                {formatCents(remainingToMin)}
              </span>{' '}
              подтверждённой комиссии, чтобы открыть вывод.
            </p>
          </div>
        )}

        <div className="relative mt-5 grid grid-cols-1 gap-4 border-t border-[var(--border-subtle)] pt-4 sm:grid-cols-3">
          <HeroSubStat
            label="В холде"
            value={formatCents(holdCents)}
            tint="var(--warning)"
            Icon={Lock}
          />
          <HeroSubStat
            label="Заявки в обработке"
            value={formatCents(inProgressCents)}
            tint="var(--info)"
            Icon={Hourglass}
          />
          <HeroSubStat
            label="Выплачено всего"
            value={formatCents(paidTotalCents)}
            tint="var(--success)"
            Icon={Banknote}
          />
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Card shell — used by the forecast panel
// ─────────────────────────────────────────────────────────────────────────

function CardShell({
  title,
  Icon,
  children,
}: {
  title: string;
  Icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <div
        aria-hidden
        className="h-[2px] w-full"
        style={{
          background:
            'linear-gradient(90deg, transparent, color-mix(in oklch, var(--accent) 55%, transparent) 30%, color-mix(in oklch, var(--accent) 55%, transparent) 70%, transparent)',
        }}
      />
      <div className="flex items-center gap-2.5 border-b border-[var(--border-subtle)] px-5 py-3.5">
        <span
          className="inline-flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)]"
          style={{
            backgroundColor: 'color-mix(in oklch, var(--accent) 14%, transparent)',
            border: '1px solid color-mix(in oklch, var(--accent) 26%, transparent)',
            color: 'var(--accent-bright)',
          }}
          aria-hidden
        >
          <Icon size={14} strokeWidth={2} />
        </span>
        <h2 className="text-[15px] font-semibold text-[var(--text)]">{title}</h2>
      </div>
      <div className="flex-1">{children}</div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Hold-release forecast
// ─────────────────────────────────────────────────────────────────────────

function ForecastCard() {
  const forecast = partnerApi.forecast.useGet();
  const data = forecast.data;
  const releases = data?.releases ?? [];

  return (
    <CardShell title="Прогноз разморозки холда" Icon={CalendarClock}>
      {forecast.isLoading ? (
        <div className="space-y-3 p-5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      ) : releases.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center px-6 py-6 text-center">
          <div
            className="grid h-12 w-12 place-items-center rounded-full"
            style={{
              backgroundColor: 'color-mix(in oklch, var(--success) 14%, var(--bg-raised))',
              border: '1px solid color-mix(in oklch, var(--success) 28%, transparent)',
              color: 'var(--success)',
            }}
          >
            <Check size={20} strokeWidth={2.5} />
          </div>
          <p className="mt-3 text-[13px] font-medium text-[var(--text)]">Холдов нет</p>
          <p className="mt-1 max-w-[260px] text-[12px] leading-relaxed text-[var(--text-muted)]">
            Подтверждённая комиссия будет появляться здесь с датой, когда она станет доступна к
            выводу.
          </p>
        </div>
      ) : (
        <div className="p-5">
          <div className="mb-4 flex items-baseline justify-between">
            <span className="text-[11px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
              Всего в холде
            </span>
            <span className="text-[18px] font-bold tabular-nums text-[var(--warning)]">
              {formatCents(data?.totalHoldCents ?? 0)}
            </span>
          </div>
          <ol className="relative space-y-3 pl-5">
            <span aria-hidden className="absolute left-[5px] top-1 bottom-1 w-px bg-[var(--border)]" />
            {releases.slice(0, 5).map((r) => (
              <li key={r.date} className="relative">
                <span
                  aria-hidden
                  className="absolute -left-[18px] top-1 h-[11px] w-[11px] rounded-full border-2 border-[var(--bg-raised)]"
                  style={{ backgroundColor: 'var(--accent-bright)' }}
                />
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-[var(--text)]">
                      {formatReleaseDate(r.date)}
                    </div>
                    <div className="text-[11px] text-[var(--text-muted)]">{relativeDay(r.date)}</div>
                  </div>
                  <span className="shrink-0 text-[14px] font-bold tabular-nums text-[var(--success)]">
                    +{formatCents(r.amountCents)}
                  </span>
                </div>
              </li>
            ))}
          </ol>
          {releases.length > 5 && (
            <p className="mt-3 text-[11px] text-[var(--text-muted)]">
              …и ещё {releases.length - 5} разморозок
            </p>
          )}
          <p className="mt-4 border-t border-[var(--border-subtle)] pt-3 text-[11px] text-[var(--text-muted)]">
            Холд — {data?.holdDays ?? 14} дней с момента подтверждения конверсии.
          </p>
        </div>
      )}
    </CardShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  History
// ─────────────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | PayoutStatus;

function HistorySkeleton() {
  return (
    <div className="space-y-3 p-5">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-9 w-9 rounded-[var(--radius-sm)]" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-28" />
        </div>
      ))}
    </div>
  );
}

function HistoryEmpty({
  canWithdraw,
  onRequest,
}: {
  canWithdraw: boolean;
  onRequest: () => void;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      <div className="relative mb-5">
        <div
          aria-hidden
          className="absolute inset-0 rounded-full blur-2xl"
          style={{
            background:
              'radial-gradient(circle, color-mix(in oklch, var(--accent) 50%, transparent), transparent 70%)',
          }}
        />
        <div
          className="relative grid h-[72px] w-[72px] place-items-center rounded-full"
          style={{
            backgroundColor: 'color-mix(in oklch, var(--accent) 14%, var(--bg-raised))',
            border: '1px solid color-mix(in oklch, var(--accent) 32%, transparent)',
            color: 'var(--accent-bright)',
          }}
        >
          <Banknote size={28} strokeWidth={2} />
        </div>
      </div>
      <h3 className="text-[18px] font-semibold text-[var(--text)]">Выплат пока не было</h3>
      <p className="mt-2 max-w-[420px] text-[13px] leading-relaxed text-[var(--text-muted)]">
        {canWithdraw
          ? 'У вас есть доступные средства. Запросите первую выплату — менеджер обработает её в течение 1–3 рабочих дней.'
          : 'Здесь появится история ваших запросов на вывод после первой выплаты.'}
      </p>
      {canWithdraw && (
        <div className="mt-5">
          <Button variant="gradient" onClick={onRequest}>
            <Send size={14} />
            Запросить выплату
          </Button>
        </div>
      )}
    </div>
  );
}

function PayoutRow({
  payout,
  index,
  onOpen,
}: {
  payout: Payout;
  index: number;
  onOpen: (p: Payout) => void;
}) {
  const sm = statusMeta(payout.status);
  const mm = methodMeta(payout.method);
  const MethodIcon = mm.Icon;
  const StatusIcon = sm.Icon;

  return (
    <button
      type="button"
      onClick={() => onOpen(payout)}
      style={rise(40 + Math.min(index, 12) * 35)}
      className="group grid w-full grid-cols-[auto_1fr_auto] items-center gap-4 border-b border-[var(--border-subtle)] px-5 py-3.5 text-left last:border-0 transition-colors hover:bg-[color-mix(in_oklch,var(--accent-bright)_5%,transparent)]"
    >
      {/* Method glyph */}
      <span
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
        style={{
          backgroundColor: 'color-mix(in oklch, var(--accent) 12%, transparent)',
          border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
          color: 'var(--accent-bright)',
        }}
        aria-hidden
      >
        <MethodIcon size={16} strokeWidth={2} />
      </span>

      {/* Amount + meta */}
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-[15px] font-bold tabular-nums text-[var(--text)]">
            {formatCents(payout.amountCents)}
          </span>
          <span className="text-[12px] text-[var(--text-muted)]">{mm.label}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-[12px] text-[var(--text-muted)]">
          <span className="font-mono">{shortAddr(payout.address)}</span>
          <span aria-hidden>·</span>
          <span className="tabular-nums whitespace-nowrap">{formatDate(payout.requestedAt)}</span>
        </div>
      </div>

      {/* Status + chevron */}
      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap"
          style={{
            backgroundColor: `color-mix(in oklch, ${sm.tint} 15%, transparent)`,
            color: sm.tint,
          }}
        >
          <StatusIcon size={12} strokeWidth={2.5} />
          {sm.label}
        </span>
        <ChevronRight
          size={16}
          className="shrink-0 text-[var(--text-quiet)] transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--text-muted)]"
        />
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Detail modal — per-payout timeline
// ─────────────────────────────────────────────────────────────────────────

function TimelineStep({
  label,
  date,
  done,
  active,
  tint,
  last,
}: {
  label: string;
  date: string | null | undefined;
  done: boolean;
  active?: boolean;
  tint: string;
  last?: boolean;
}) {
  return (
    <li className="relative flex gap-3 pb-4 last:pb-0">
      {!last && (
        <span
          aria-hidden
          className="absolute left-[9px] top-5 bottom-0 w-px"
          style={{ backgroundColor: done ? tint : 'var(--border)' }}
        />
      )}
      <span
        aria-hidden
        className="relative z-10 grid h-[19px] w-[19px] shrink-0 place-items-center rounded-full"
        style={{
          backgroundColor: done ? tint : 'var(--bg-elevated)',
          border: `1px solid ${done ? tint : 'var(--border-strong)'}`,
          color: 'var(--accent-fg)',
        }}
      >
        {done && <Check size={11} strokeWidth={3} />}
      </span>
      <div className="min-w-0 -mt-0.5">
        <div
          className={
            'text-[13px] font-medium ' +
            (done || active ? 'text-[var(--text)]' : 'text-[var(--text-muted)]')
          }
        >
          {label}
        </div>
        <div className="text-[12px] tabular-nums text-[var(--text-muted)]">
          {date ? formatDate(date) : '—'}
        </div>
      </div>
    </li>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="pt-0.5 text-[12px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
        {label}
      </span>
      <div className="min-w-0 text-right text-[13px] text-[var(--text)]">{children}</div>
    </div>
  );
}

function PayoutDetailModal({
  payout,
  onClose,
}: {
  payout: Payout | null;
  onClose: () => void;
}) {
  const open = payout != null;
  const sm = statusMeta(payout?.status);
  const mm = methodMeta(payout?.method);
  const isDeclined = payout?.status === 'declined';
  const isFailed = payout?.status === 'failed';
  const isPaid = payout?.status === 'paid';
  const isProcessed = isPaid || isDeclined || isFailed || payout?.status === 'approved';
  const StatusIcon = sm.Icon;
  const MethodIcon = mm.Icon;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={payout ? `Выплата #${payout.id}` : 'Выплата'}
      subtitle={payout ? mm.label : undefined}
      width="460px"
    >
      {payout && (
        <div className="space-y-5">
          {/* Amount + status banner */}
          <div
            className="rounded-[var(--radius-md)] border p-4"
            style={{
              borderColor: `color-mix(in oklch, ${sm.tint} 32%, transparent)`,
              backgroundColor: `color-mix(in oklch, ${sm.tint} 8%, var(--bg-raised))`,
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <div
                className="text-[28px] font-bold tabular-nums leading-none text-[var(--text)]"
                style={{ letterSpacing: 'var(--tracking-tight)' }}
              >
                {formatCents(payout.amountCents)}
              </div>
              <span
                className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
                style={{
                  backgroundColor: `color-mix(in oklch, ${sm.tint} 16%, transparent)`,
                  color: sm.tint,
                }}
              >
                <StatusIcon size={12} strokeWidth={2.5} />
                {sm.label}
              </span>
            </div>
            {sm.hint && (
              <p className="mt-2 text-[12px] leading-relaxed text-[var(--text-secondary)]">
                {sm.hint}
              </p>
            )}
          </div>

          {/* Failure reason */}
          {isFailed && payout.failureReason && (
            <div className="rounded-[var(--radius-md)] bg-[var(--danger-soft)] px-3.5 py-3 text-[12px] leading-relaxed text-[var(--danger)]">
              <span className="font-semibold">Причина: </span>
              {payout.failureReason}
            </div>
          )}

          {/* Requisites */}
          <div className="divide-y divide-[var(--border-subtle)] rounded-[var(--radius-md)] border border-[var(--border-subtle)] px-4">
            <DetailRow label="Метод">
              <span className="inline-flex items-center gap-1.5">
                <MethodIcon size={13} className="text-[var(--accent-bright)]" />
                {mm.label}
              </span>
            </DetailRow>
            <DetailRow label="Реквизиты">
              <div className="flex items-center justify-end gap-2">
                <span className="break-all font-mono text-[12px]">{payout.address ?? '—'}</span>
                {payout.address && <CopyButton value={payout.address} size="xs" />}
              </div>
            </DetailRow>
            {payout.network && (
              <DetailRow label="Сеть / мемо">
                <span className="font-mono text-[12px]">{payout.network}</span>
              </DetailRow>
            )}
            {isPaid && payout.externalTxId && (
              <DetailRow label="Транзакция">
                <div className="flex items-center justify-end gap-2">
                  <span className="break-all font-mono text-[12px]">{payout.externalTxId}</span>
                  <CopyButton value={payout.externalTxId} size="xs" />
                </div>
              </DetailRow>
            )}
          </div>

          {/* Timeline */}
          <div>
            <div className="mb-2.5 text-[12px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
              Хронология
            </div>
            <ol className="pl-0.5">
              <TimelineStep
                label="Заявка создана"
                date={payout.requestedAt}
                done
                tint="var(--accent-bright)"
              />
              <TimelineStep
                label={isDeclined ? 'Заявка отклонена' : 'Рассмотрена менеджером'}
                date={payout.processedAt}
                done={isProcessed}
                active={payout.status === 'pending'}
                tint={isDeclined ? 'var(--danger)' : 'var(--accent-bright)'}
                last={isDeclined}
              />
              {!isDeclined && (
                <TimelineStep
                  label={isFailed ? 'Ошибка перевода' : 'Средства отправлены'}
                  date={payout.paidAt}
                  done={isPaid}
                  active={payout.status === 'approved'}
                  tint={isFailed ? 'var(--danger)' : 'var(--success)'}
                  last
                />
              )}
            </ol>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Request modal
// ─────────────────────────────────────────────────────────────────────────

function makeSchema(maxCents: number, minCents: number) {
  return z.object({
    amount: z
      .string()
      .min(1, 'Укажите сумму')
      .refine(
        (v) => !Number.isNaN(parseFloat(v)) && parseFloat(v) > 0,
        'Должно быть положительным числом',
      )
      .refine(
        (v) => minCents <= 0 || Math.round(parseFloat(v) * 100) >= minCents,
        `Минимальная сумма вывода — ${formatCents(minCents)}`,
      )
      .refine(
        (v) => Math.round(parseFloat(v) * 100) <= maxCents,
        'Превышает доступный баланс',
      ),
    method: z.enum(['usdt_trc20', 'usdt_bep20']),
    address: z.string().trim().min(3, 'Укажите адрес кошелька'),
    network: z.string().trim().max(64).optional(),
  });
}

function RequestPayoutModal({
  open,
  onClose,
  availableCents,
  minCents,
  defaultMethod,
  defaultAddress,
}: {
  open: boolean;
  onClose: () => void;
  availableCents: number;
  minCents: number;
  defaultMethod: RequestMethod;
  defaultAddress: string;
}) {
  const createPayout = partnerApi.payouts.useCreate();
  const schema = useMemo(() => makeSchema(availableCents, minCents), [availableCents, minCents]);
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, formState, reset, watch, setValue } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { method: defaultMethod, address: defaultAddress, amount: '', network: '' },
  });

  // Re-seed defaults each time the modal opens.
  useEffect(() => {
    if (open) {
      reset({ method: defaultMethod, address: defaultAddress, amount: '', network: '' });
    }
  }, [open, defaultMethod, defaultAddress, reset]);

  const method = watch('method');
  const amountRaw = watch('amount');
  const amountCents = Math.round((parseFloat(amountRaw || '0') || 0) * 100);
  const remainingAfter = availableCents - amountCents;
  const showNetwork = methodMeta(method).crypto;

  const setAmountCents = (cents: number) => {
    setValue('amount', (cents / 100).toFixed(2), { shouldValidate: true, shouldDirty: true });
  };

  const quickFills: Array<{ label: string; cents: number }> = [
    { label: `Мин · ${formatCents(minCents)}`, cents: minCents },
    { label: '50%', cents: Math.round(availableCents / 2) },
    { label: `Макс · ${formatCents(availableCents)}`, cents: availableCents },
  ];

  const onSubmit = async (v: FormValues) => {
    await createPayout.mutateAsync({
      amountCents: Math.round(parseFloat(v.amount) * 100),
      method: v.method as PayoutMethod,
      address: v.address.trim(),
      network: v.network?.trim() ? v.network.trim() : undefined,
    });
    toast.success('Заявка на выплату создана', {
      description: 'Менеджер обработает её в течение 1–3 рабочих дней.',
    });
    reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Запрос на выплату"
      subtitle={`Доступно к выводу: ${formatCents(availableCents)}`}
      width="540px"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-[12px] text-[var(--text-muted)]">
            Остаток после выплаты:{' '}
            <span
              className={
                'font-semibold tabular-nums ' +
                (remainingAfter < 0 ? 'text-[var(--danger)]' : 'text-[var(--text-secondary)]')
              }
            >
              {formatCents(Math.max(0, remainingAfter))}
            </span>
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button
              type="submit"
              form="payout-form"
              loading={createPayout.isPending}
              variant="gradient"
            >
              <Send size={14} />
              {createPayout.isPending ? 'Отправка…' : 'Запросить'}
            </Button>
          </div>
        </div>
      }
    >
      <form id="payout-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Amount */}
        <div>
          <Label htmlFor="amount">Сумма (USD)</Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            min="0.01"
            inputMode="decimal"
            placeholder="0.00"
            className="text-[16px] tabular-nums"
            {...register('amount')}
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {quickFills.map((q) => (
              <button
                key={q.label}
                type="button"
                onClick={() => setAmountCents(q.cents)}
                disabled={q.cents <= 0 || q.cents > availableCents}
                className="rounded-[var(--radius-sm)] border border-[var(--border-strong)] bg-[var(--bg-elevated)] px-2.5 py-1 text-[11px] font-medium text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-soft)] hover:text-[var(--text)] disabled:pointer-events-none disabled:opacity-40"
              >
                {q.label}
              </button>
            ))}
          </div>
          <FieldError message={formState.errors.amount?.message} />
        </div>

        {/* Method picker */}
        <div>
          <Label>Метод выплаты</Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {METHOD_ORDER.map((m) => {
              const meta = METHOD_META[m];
              const MIcon = meta.Icon;
              const selected = method === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setValue('method', m, { shouldValidate: true })}
                  aria-pressed={selected}
                  className={
                    'flex items-center gap-2 rounded-[var(--radius-md)] border px-3 py-2.5 text-left transition-[border,background,box-shadow] ' +
                    (selected
                      ? 'border-[var(--accent)] bg-[var(--accent-soft)] shadow-[0_0_0_3px_var(--accent-soft)]'
                      : 'border-[var(--border-strong)] bg-[var(--bg-base)] hover:border-[var(--accent-soft)]')
                  }
                >
                  <MIcon
                    size={16}
                    className={selected ? 'text-[var(--accent-bright)]' : 'text-[var(--text-muted)]'}
                  />
                  <span
                    className={
                      'text-[12px] font-medium leading-tight ' +
                      (selected ? 'text-[var(--text)]' : 'text-[var(--text-secondary)]')
                    }
                  >
                    {meta.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Address */}
        <div>
          <Label htmlFor="address">Адрес / реквизиты</Label>
          <Input
            id="address"
            placeholder={methodMeta(method).crypto ? 'Адрес кошелька…' : 'IBAN / номер карты…'}
            className="font-mono text-[13px]"
            {...register('address')}
          />
          <FieldError message={formState.errors.address?.message} />
        </div>

        {/* Network — crypto only */}
        {showNetwork && (
          <div>
            <Label htmlFor="network">Сеть / мемо (опционально)</Label>
            <Input
              id="network"
              placeholder="Напр.: TRC20, ERC20, memo-тег…"
              {...register('network')}
            />
          </div>
        )}

        <div className="flex items-start gap-2 rounded-[var(--radius-md)] bg-[var(--info-soft)] px-3.5 py-2.5 text-[12px] leading-relaxed text-[var(--info)]">
          <Sparkles size={14} className="mt-0.5 shrink-0" />
          <span>
            Проверьте реквизиты — после одобрения выплата уходит на них без возможности отмены.
          </span>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────────────

const FILTERS: Array<{ id: StatusFilter; label: string }> = [
  { id: 'all', label: 'Все' },
  { id: 'pending', label: 'На рассмотрении' },
  { id: 'approved', label: 'Одобрены' },
  { id: 'paid', label: 'Выплачены' },
  { id: 'declined', label: 'Отклонены' },
  { id: 'failed', label: 'Ошибки' },
];

export function PayoutsPage() {
  const payouts = partnerApi.payouts.useList({ pageSize: 100 });
  const balance = partnerApi.balance.useGet();
  const minProgress = partnerApi.minPayoutProgress.useGet();
  const me = partnerApi.me.useGet();

  const [requestOpen, setRequestOpen] = useState(false);
  const [detail, setDetail] = useState<Payout | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');

  const items = payouts.data?.items ?? [];
  const available = balance.data?.availableCents ?? 0;
  const holdCents = balance.data?.holdCents ?? 0;
  const minPayoutCents = minProgress.data?.minPayoutCents ?? 0;
  const canWithdraw = available > 0 && available >= minPayoutCents;

  // Derived totals from the loaded history.
  const inProgressCents = useMemo(
    () =>
      items
        .filter((p) => p.status === 'pending' || p.status === 'approved')
        .reduce((s, p) => s + (p.amountCents ?? 0), 0),
    [items],
  );
  const paidTotalCents = useMemo(
    () => items.filter((p) => p.status === 'paid').reduce((s, p) => s + (p.amountCents ?? 0), 0),
    [items],
  );

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: items.length,
      pending: 0,
      approved: 0,
      paid: 0,
      declined: 0,
      failed: 0,
    };
    for (const p of items) {
      if (p.status in c) c[p.status as StatusFilter] += 1;
    }
    return c;
  }, [items]);

  const filtered = useMemo(
    () => (filter === 'all' ? items : items.filter((p) => p.status === filter)),
    [items, filter],
  );

  // Дефолт — сохранённый метод партнёра, но только если это TRC20/BEP20; иначе TRC20.
  const storedMethod = me.data?.paymentMethod;
  const defaultMethod: RequestMethod =
    storedMethod === 'usdt_trc20' || storedMethod === 'usdt_bep20' ? storedMethod : 'usdt_trc20';
  const defaultAddress = me.data?.paymentAddress ?? '';

  const loading = payouts.isLoading || balance.isLoading;
  const isEmpty = !payouts.isLoading && items.length === 0;

  return (
    <div className="relative mx-auto max-w-[1180px]">
      <style>{`
        @keyframes payRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes payFade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[-120px] -z-10 h-[360px] w-[760px] -translate-x-1/2"
        style={{
          background:
            'radial-gradient(ellipse at center, color-mix(in oklch, var(--accent) 20%, transparent), transparent 68%)',
          filter: 'blur(36px)',
        }}
      />

      <div style={rise(0)}>
        <PageHeader
          eyebrow="Партнёрская программа"
          title="Выплаты"
          subtitle="Запрос вывода, прогноз разморозки холда и история всех выплат."
          actions={
            <Button onClick={() => setRequestOpen(true)} disabled={!canWithdraw} variant="gradient">
              <Send size={14} />
              Запросить выплату
            </Button>
          }
        />
      </div>

      {/* Hero */}
      <div className="mb-5">
        {balance.isLoading ? (
          <Skeleton className="h-[232px] w-full rounded-[var(--radius-lg)]" />
        ) : (
          <PayoutHero
            availableCents={available}
            holdCents={holdCents}
            inProgressCents={inProgressCents}
            paidTotalCents={paidTotalCents}
            minPayoutCents={minPayoutCents}
            canWithdraw={canWithdraw}
            onRequest={() => setRequestOpen(true)}
            style={rise(70)}
          />
        )}
      </div>

      {/* Forecast + auto-payout */}
      <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div style={fade(150)}>
          <ForecastCard />
        </div>
        <div style={fade(210)}>
          <AutoPayoutToggle />
        </div>
      </div>

      {/* History */}
      <Card className="overflow-hidden" style={fade(270)}>
        <div
          aria-hidden
          className="h-[2px] w-full"
          style={{
            background:
              'linear-gradient(90deg, transparent, color-mix(in oklch, var(--accent) 60%, transparent) 30%, color-mix(in oklch, var(--accent) 60%, transparent) 70%, transparent)',
          }}
        />
        <div className="flex flex-wrap items-center gap-2.5 border-b border-[var(--border-subtle)] px-5 py-3.5">
          <span
            className="inline-flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)]"
            style={{
              backgroundColor: 'color-mix(in oklch, var(--accent) 14%, transparent)',
              border: '1px solid color-mix(in oklch, var(--accent) 26%, transparent)',
              color: 'var(--accent-bright)',
            }}
            aria-hidden
          >
            <Banknote size={14} strokeWidth={2} />
          </span>
          <h2 className="text-[15px] font-semibold text-[var(--text)]">История выплат</h2>
          {items.length > 0 && (
            <span className="rounded-[var(--radius-pill)] bg-[var(--bg-elevated)] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-[var(--text-muted)]">
              {items.length}
            </span>
          )}
        </div>

        {/* Filter chips */}
        {!loading && items.length > 0 && (
          <div className="flex flex-wrap gap-1.5 border-b border-[var(--border-subtle)] px-5 py-3">
            {FILTERS.filter((f) => f.id === 'all' || counts[f.id] > 0).map((f) => {
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  className={
                    'inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] px-3 py-1 text-[12px] font-medium transition-colors ' +
                    (active
                      ? 'bg-[var(--accent)] text-[var(--accent-fg)]'
                      : 'bg-[var(--bg-elevated)] text-[var(--text-muted)] hover:text-[var(--text)]')
                  }
                >
                  {f.label}
                  <span
                    className={
                      'tabular-nums ' +
                      (active ? 'text-[var(--accent-fg)] opacity-75' : 'text-[var(--text-quiet)]')
                    }
                  >
                    {counts[f.id]}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {loading && <HistorySkeleton />}
        {isEmpty && (
          <HistoryEmpty canWithdraw={canWithdraw} onRequest={() => setRequestOpen(true)} />
        )}
        {!loading && items.length > 0 && filtered.length === 0 && (
          <div className="px-6 py-12 text-center text-[13px] text-[var(--text-muted)]">
            Нет выплат с этим статусом.
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div>
            {filtered.map((p, i) => (
              <PayoutRow key={p.id} payout={p} index={i} onOpen={setDetail} />
            ))}
          </div>
        )}
      </Card>

      <RequestPayoutModal
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        availableCents={available}
        minCents={minPayoutCents}
        defaultMethod={defaultMethod}
        defaultAddress={defaultAddress}
      />
      <PayoutDetailModal payout={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
